<script setup lang="ts">
/**
 * Полный поиск — макеты «Поиск · /search» 349-3679 и mobile 345-20882.
 *
 * Тонкий роут над существующими ручками (решение консилиума, DEV-2050):
 * SearchIssues НЕ расширяется. Без проекта работает чистый текстовый поиск по
 * всем видимым проектам; выбранный проект включает полные фильтры — под ними
 * работает обычный ListIssues. Это ровно те же правила, что у MCP-инструмента:
 * «фильтры по полям работают внутри проекта».
 */
import { AppButton } from '@vinprop/design-system'
import { AlertTriangle, Search, SearchX } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import {
  listIssues,
  searchIssuesPage,
  typesOfProfile,
  type IssueRow,
  type ShowTab,
} from '@/features/issues/api'
import { emptyFilters } from '@/features/issues/filters'
import { formatDueDate } from '@/features/issues/format'
import { useProjectsStore } from '@/features/projects/store'

import type { V1IssueType } from '@/api/clients/tracker-issue'

const PER_PAGE = 25

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const projects = useProjectsStore()

const query = ref(String(route.query.q ?? ''))
const projectId = ref(String(route.query.project ?? ''))
const types = ref<V1IssueType[]>(
  String(route.query.type ?? '')
    .split(',')
    .filter(Boolean) as V1IssueType[],
)
const tab = ref<ShowTab>(
  ['open', 'done', 'all'].includes(String(route.query.tab)) ? (String(route.query.tab) as ShowTab) : 'all',
)

const rows = ref<IssueRow[]>([])
const total = ref(0)
const page = ref(1)
const isLoading = ref(false)
const error = ref<unknown>(null)

const projectOptions = computed(() =>
  projects.items.map((project) => ({
    value: project.id,
    label: `${project.name} · ${project.key}`,
  })),
)

const currentProject = computed(() =>
  projects.items.find((project) => project.id === projectId.value),
)

const typeOptions = computed(() =>
  typesOfProfile(currentProject.value?.typeProfile ?? 'TYPE_PROFILE_DEVELOPMENT').map((value) => ({
    value,
    label: t(`issues.types.${value}`),
  })),
)

const tabOptions = computed(() =>
  (['open', 'done', 'all'] as ShowTab[]).map((value) => ({
    value,
    label: t(`issues.list.show.${value}`),
  })),
)

/** Фильтры живут внутри проекта — как у сервера, чипы честно гаснут без него */
const filtersEnabled = computed(() => Boolean(projectId.value))

const load = async (append = false) => {
  const needle = query.value.trim()

  if (!needle) {
    rows.value = []
    total.value = 0

    return
  }

  isLoading.value = true
  error.value = null

  try {
    const result = projectId.value
      ? await listIssues(
          projectId.value,
          { ...emptyFilters(), search: needle, types: types.value },
          { field: 'updated_at', desc: true },
          page.value,
          PER_PAGE,
          tab.value,
        )
      : await searchIssuesPage(needle, page.value, PER_PAGE)

    rows.value = append ? [...rows.value, ...result.issues] : result.issues
    total.value = result.total
  } catch (failure) {
    error.value = failure
  } finally {
    isLoading.value = false
  }
}

const loadMore = async () => {
  page.value += 1

  await load(true)
}

/** Срез живёт в адресе: ссылку на поиск пересылают (DEV-2040) */
const writeQuery = () => {
  const next: Record<string, string> = {}

  if (query.value.trim()) next.q = query.value.trim()
  if (projectId.value) next.project = projectId.value
  if (types.value.length) next.type = types.value.join(',')
  if (tab.value !== 'all') next.tab = tab.value

  void router.replace({ query: next })
}

let timer: ReturnType<typeof setTimeout> | undefined

watch(query, () => {
  if (timer) clearTimeout(timer)

  timer = setTimeout(() => {
    page.value = 1
    writeQuery()
    void load()
  }, 400)
})

watch([projectId, types, tab], () => {
  // Смена проекта гасит недоступные без него фильтры.
  if (!projectId.value) {
    types.value = []
    tab.value = 'all'
  }

  page.value = 1
  writeQuery()
  void load()
})

const dueLabel = (issue: IssueRow) => formatDueDate(issue.dueDate, locale.value)

onMounted(async () => {
  await projects.ensureLoaded()
  await load()
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('views.search') }]" />

  <div class="srch">
    <label class="srch__field">
      <Search :size="16" />
      <input
        v-model="query"
        type="search"
        :placeholder="t('search.placeholder')"
        :aria-label="t('views.search')"
      />
    </label>

    <div class="srch__chips">
      <!-- Проект — одиночный выбор: чип отдаёт последнее нажатое. -->
      <FilterChip
        :model-value="projectId ? [projectId] : []"
        :label="t('my.filters.project')"
        :options="projectOptions"
        :empty-text="t('toolbar.filters.empty')"
        @update:model-value="projectId = $event[$event.length - 1] ?? ''"
      />

      <template v-if="filtersEnabled">
        <FilterChip
          :model-value="types"
          :label="t('toolbar.filters.type')"
          :options="typeOptions"
          :empty-text="t('toolbar.filters.empty')"
          @update:model-value="types = $event as V1IssueType[]"
        />

        <FilterChip
          :model-value="[tab]"
          :label="t('issues.list.show.label')"
          :options="tabOptions"
          :empty-text="t('toolbar.filters.empty')"
          @update:model-value="tab = ($event[$event.length - 1] as ShowTab) ?? 'all'"
        />
      </template>

      <!-- Фильтры по полям работают внутри проекта — то же правило, что у
           сервера: молча сузить нечем, честнее сказать. -->
      <span v-else class="srch__hint">{{ t('search.pickProject') }}</span>
    </div>

    <div v-if="isLoading && !rows.length" class="srch__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('issues.list.error.title')"
      :description="errorMessage(error, t('issues.list.error.description'))"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load()">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!rows.length && query.trim()"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('search.tryOther')"
    />

    <p v-else-if="!rows.length" class="srch__empty">{{ t('search.start') }}</p>

    <template v-else>
      <div class="srch__list">
        <RouterLink v-for="issue in rows" :key="issue.id" class="srow" :to="`/issue/${issue.key}`">
          <IssueTypeIcon :type="issue.type" />
          <span class="srow__key">{{ issue.key }}</span>
          <span class="srow__title">{{ issue.title }}</span>
          <span class="srow__due">{{ dueLabel(issue) }}</span>
        </RouterLink>
      </div>

      <footer class="srch__foot">
        <span>{{ t('issues.list.shown', { shown: rows.length, total }) }}</span>

        <button v-if="rows.length < total" type="button" class="srch__more" @click="loadMore">
          {{ t('issues.list.showMore', { count: total - rows.length }) }}
        </button>
      </footer>
    </template>
  </div>
</template>

<style scoped>
.srch {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  max-width: 920px;
  width: 100%;
  margin: 0 auto;
  padding: 20px 16px;
}

.srch__field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  color: var(--color-text-muted);
}

.srch__field input {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body);
  outline: none;
}

.srch__chips {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
}

.srch__hint {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.srch__skeleton {
  height: 240px;
  border-radius: var(--radius-lg);
  background: var(--color-bg-muted);
}

.srch__empty {
  margin: 32px 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
  text-align: center;
}

.srch__list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.srow {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 16px;
  border-top: 1px solid var(--color-border);
  color: inherit;
  text-decoration: none;
}

.srow:first-child {
  border-top: none;
}

.srow:hover {
  background: var(--color-bg-muted);
}

.srow__key {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

.srow__title {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.srow__due {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.srch__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.srch__more {
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
